import type { ReactNode } from 'react'
import type { ScormData } from '~/utils/scorm'
import { CheckCircle2, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { useBranding } from '~/helpers/branding/branding'
import { cn } from '~/lib/utils'
import { createScorm12Adapter, ScormApiBridge } from '~/utils/scorm'

/** Délai avant redirection automatique après la fin du module. */
const FINISHED_REDIRECT_DELAY_MS = 5000

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    API_1484_11?: ScormApiBridge
    API?: ReturnType<typeof createScorm12Adapter>
  }
}

type ScormShellProps = {
  /** Chargement des données amont (queries) : affiche l'écran d'attente. */
  loading: boolean
  module?: { id: string, status: string, launchUrl?: string | null } | null
  /** Tracking initial injecté dans le bridge (null = départ vierge). */
  initialData: ScormData | null
  /**
   * Persistance des commits SCORM. Doit être référentiellement stable
   * (useCallback ou constante module) : le bridge est remonté quand elle change.
   */
  onCommit: (data: ScormData) => Promise<void>
  /** Contenu additionnel du bandeau, à droite du nom de l'app. */
  headerContent?: ReactNode
  headerRight?: ReactNode
  /** Lien de repli affiché quand le module est indisponible. */
  fallbackTo: string
  fallbackLabel: string
  unavailableMessage: string
}

/**
 * Coquille commune du player SCORM (apprenant et prévisualisation) : vérifie
 * la disponibilité du contenu, monte les API SCORM 1.2/2004 dans window et
 * rend le bandeau + l'iframe (ou les écrans de chargement/indisponibilité).
 */
function ScormShell({
  loading,
  module,
  initialData,
  onCommit,
  headerContent,
  headerRight,
  fallbackTo,
  fallbackLabel,
  unavailableMessage,
}: ScormShellProps) {
  const { t } = useTranslation()
  const { appName } = useBranding()
  const navigate = useNavigate()

  // Renseigné quand le contenu appelle Terminate : le module a rendu la main
  // (son propre écran de fin dit « fermez la fenêtre », inutile en iframe).
  // On le remplace par notre écran de fin + redirection vers fallbackTo.
  const [finished, setFinished] = useState<{ completed: boolean } | null>(null)

  const canPlay = Boolean(module && module.status === 'READY' && module.launchUrl)
  const contentUrl = canPlay ? `/modules/${module!.id}/content/${module!.launchUrl}` : null

  // Garde-fou : vérifie que le contenu est réellement servi (fichiers présents)
  // avant d'afficher l'iframe, pour éviter d'exposer un JSON 404 brut.
  const [contentStatus, setContentStatus] = useState<'checking' | 'ok' | 'missing'>('checking')
  useEffect(() => {
    if (!contentUrl) return
    let cancelled = false
    setContentStatus('checking')
    fetch(contentUrl, { method: 'HEAD', credentials: 'include' })
      .then((res) => {
        if (!cancelled) setContentStatus(res.ok ? 'ok' : 'missing')
      })
      .catch(() => {
        if (!cancelled) setContentStatus('missing')
      })
    return () => { cancelled = true }
  }, [contentUrl])

  useEffect(() => {
    if (loading || !canPlay || contentStatus !== 'ok') return

    // Ignore le Terminate déclenché par le unload de l'iframe quand on quitte
    // nous-mêmes la page (bouton retour du bandeau, navigation…).
    let active = true
    const bridge = new ScormApiBridge(initialData, onCommit, (info) => {
      if (active) setFinished(info)
    })

    // Mount SCORM 2004 API
    window.API_1484_11 = bridge
    // Mount SCORM 1.2 adapter
    window.API = createScorm12Adapter(bridge)

    // À la fermeture de l'onglet, attend les mutations de tracking en vol pour
    // leur donner une longueur d'avance avant que le navigateur tue la page.
    const flushOnPageHide = () => {
      void bridge.flushCommits()
    }
    window.addEventListener('pagehide', flushOnPageHide)

    return () => {
      active = false
      window.removeEventListener('pagehide', flushOnPageHide)
      delete window.API_1484_11
      delete window.API
    }
  }, [loading, canPlay, contentStatus, initialData, onCommit])

  useEffect(() => {
    if (!finished) return
    const timer = setTimeout(() => navigate(fallbackTo), FINISHED_REDIRECT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [finished, navigate, fallbackTo])

  if (loading) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-background">
        <Loader className="size-6 animate-spin text-muted-foreground" />
        <p className="font-heading text-lg tracking-wide">{t('scorm.player.loading')}</p>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-black">
      <div className="flex items-center justify-between px-6 py-3 bg-black text-white border-b border-neutral-800">
        <div className="flex items-center gap-4">
          <span className="font-heading text-xs tracking-[0.3em] uppercase">{appName}</span>
          {headerContent}
        </div>
        {headerRight}
      </div>
      <div className="flex-1">
        {!canPlay || contentStatus === 'missing'
          ? (
              <div className="flex h-full flex-col items-center justify-center gap-4 bg-background px-6 text-center">
                <p className="text-lg">
                  {contentStatus === 'missing'
                    ? t('scorm.player.contentError')
                    : unavailableMessage}
                </p>
                <Button variant="outline" onClick={() => navigate(fallbackTo)}>
                  {fallbackLabel}
                </Button>
              </div>
            )
          : contentStatus === 'checking'
            ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">
                  <Loader className="size-6 animate-spin text-muted-foreground" />
                  <p className="font-heading text-lg tracking-wide">{t('scorm.player.loading')}</p>
                </div>
              )
            : finished
              ? (
                  <div className="flex h-full items-center justify-center bg-background px-6">
                    <div className="flex max-w-sm flex-col items-center gap-4 text-center">
                      <div className={cn(
                        'flex size-16 items-center justify-center rounded-full',
                        finished.completed ? 'bg-green-500/10' : 'bg-muted',
                      )}
                      >
                        <CheckCircle2 className={cn('size-9', finished.completed ? 'text-green-500' : 'text-muted-foreground')} />
                      </div>
                      <h2 className="font-heading text-2xl text-balance">
                        {finished.completed ? t('scorm.finished.completedTitle') : t('scorm.finished.title')}
                      </h2>
                      <p className="text-sm text-muted-foreground">{t('scorm.finished.redirect')}</p>
                      <Button className="mt-1" onClick={() => navigate(fallbackTo)}>
                        {fallbackLabel}
                      </Button>
                    </div>
                  </div>
                )
              : (
                  <iframe
                    src={contentUrl!}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
                    className="w-full h-full border-0"
                    title={t('scorm.player.title')}
                    allow="fullscreen"
                  />
                )}
      </div>
    </div>
  )
}

export default ScormShell
